import type { Template } from '../types/interfaces'
import { MessageStatus, MessageType } from '../types/enums'
import { parseUtcDate } from '../composables/useTimeFormatter'

interface TemplatePreviewData {
  template_id?: string
  template_name?: string
  params?: string[]
}

const toPreviewText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  return null
}

const parseTemplatePreviewData = (content?: string): TemplatePreviewData | null => {
  if (!content) {
    return null
  }

  try {
    return JSON.parse(content) as TemplatePreviewData
  } catch {
    return null
  }
}

const replaceTemplateParams = (text: string, params: string[]): string => {
  let result = text
  params.forEach((param, index) => {
    result = result.replace(`{{${index + 1}}}`, param)
  })
  return result
}

export const getMessagePreviewText = (
  messageContent?: {
    type?: string
    content?: string
    text?: string
    caption?: string | null
    file_name?: string | null
  } | null,
  resolveTemplateById?: (id: string) => Template | undefined,
  resolveTemplateByName?: (name: string) => Template | undefined,
): string => {
  if (!messageContent) {
    return 'Archivo'
  }

  if (messageContent.type === MessageType.INTERACTION) {
    const raw = toPreviewText(messageContent.content) || toPreviewText(messageContent.text) || ''
    return raw.replace(
      /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g,
      (iso) => {
        const d = parseUtcDate(iso)
        if (Number.isNaN(d.getTime())) return iso
        return d.toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      },
    )
  }

  if (messageContent.type === MessageType.TEMPLATE) {
    const templateData = parseTemplatePreviewData(messageContent.content)

    const resolvedTemplate =
      (templateData?.template_id && resolveTemplateById
        ? resolveTemplateById(templateData.template_id)
        : undefined) ||
      (templateData?.template_name && resolveTemplateByName
        ? resolveTemplateByName(templateData.template_name)
        : undefined)

    if (resolvedTemplate?.content.header?.content) {
      return replaceTemplateParams(resolvedTemplate.content.header.content, templateData?.params || [])
    }

    if (templateData?.template_name) {
      return templateData.template_name
    }

    return 'Template enviado'
  }

  return (
    toPreviewText(messageContent.content) ||
    toPreviewText(messageContent.text) ||
    toPreviewText(messageContent.caption) ||
    toPreviewText(messageContent.file_name) ||
    'Archivo'
  )
}

export const normalizeMessageStatusValue = (status: unknown): MessageStatus | null => {
  const normalizedStatus = String(status || '').toUpperCase()

  switch (normalizedStatus) {
    case MessageStatus.PENDING:
      return MessageStatus.PENDING
    case MessageStatus.ENQUEUED:
      return MessageStatus.ENQUEUED
    case MessageStatus.SENT:
      return MessageStatus.SENT
    case MessageStatus.SEND:
      return MessageStatus.SEND
    case MessageStatus.DELIVERED:
      return MessageStatus.DELIVERED
    case MessageStatus.READ:
      return MessageStatus.READ
    case MessageStatus.FAILED:
      return MessageStatus.FAILED
    default:
      return null
  }
}
